import { forwardRef } from 'react';
import ReactSelect, {
  type GroupBase,
  type Props as ReactSelectProps,
  type SelectInstance,
} from 'react-select';
import { buildSelectClassNames } from './select.classNames';

export interface SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> extends ReactSelectProps<Option, IsMulti, Group> {
  size?: 'sm' | 'md' | 'lg';
}

const SelectInner = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  { size = 'md', classNames: userClassNames, unstyled = true, ...props }: SelectProps<Option, IsMulti, Group>,
  ref: React.Ref<SelectInstance<Option, IsMulti, Group>>,
) => (
  <ReactSelect
    ref={ref}
    unstyled={unstyled}
    classNames={{ ...buildSelectClassNames<Option, IsMulti, Group>({ size }), ...userClassNames }}
    {...props}
  />
);

/**
 * Token-driven wrapper around react-select. Use directly inside a `<FormField>`
 * for accessibility (Label, error, helper text). Pass `inputId` matching the
 * FormField id.
 */
export const Select = forwardRef(SelectInner) as <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectProps<Option, IsMulti, Group> & { ref?: React.Ref<SelectInstance<Option, IsMulti, Group>> },
) => ReturnType<typeof SelectInner>;
